import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Chip, Grid, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, CircularProgress, Button
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  aiCallApi, useInterval, formatBdt, formatNumber, pct, ratio, dhaka, isObjectId,
  OUTCOMES, CALL_STATES, PURPOSES, DAY_NAMES, reasonText
} from "./useAICalling";
import { SectionCard, Metric, Step, ErrorState } from "./AICallingComponents";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const moneyColor = (n) => (Number(n) >= 0 ? "success.main" : "error.main");

/* ------------------------------------------------------------------ funnel */

/**
 * dialed → answered → talked ≥30 s → link sent → link clicked → paid → revenue → cost → profit
 * smsCost (campaign pilots) is added to the cost.
 */
export const FunnelRow = ({ funnel }) => {
  if (!funnel) return null;
  const f = funnel;
  const smsCost = Number(f.smsCost) || 0;
  const cost = round2((Number(f.cost) || 0) + smsCost);
  const profit = round2((Number(f.revenue) || 0) - cost);
  const of = (a, b, what) => {
    const r = ratio(a, b);
    return r ? `${r} of ${what}` : undefined;
  };
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        <Step label="Dialed" value={f.dialed || 0} />
        <Step label="Answered" value={f.answered || 0} sub={of(f.answered, f.dialed, "dialed")} />
        <Step label="Talked ≥30 s" value={f.talked30 || 0} sub={of(f.talked30, f.answered, "answered")} />
        <Step label="Link sent" value={f.linkSent || 0} sub={of(f.linkSent, f.answered, "answered")} />
        <Step label="Link clicked" value={f.linkClicked || 0} sub={of(f.linkClicked, f.linkSent, "links")} />
        <Step label="Paid" value={f.paid || 0} sub={of(f.paid, f.answered, "answered")} />
        <Step label="Revenue" value={formatBdt(f.revenue)} />
        <Step
          label="Cost"
          value={formatBdt(cost)}
          sub={smsCost ? `Calls ${formatBdt(f.cost)} + SMS ${formatBdt(smsCost)}` : "AI + phone line"}
        />
        <Step label={profit >= 0 ? "Profit" : "Loss"} value={formatBdt(Math.abs(profit))} color={moneyColor(profit)} sub="Revenue − cost" />
      </Box>
      {f.blocked > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {formatNumber(f.blocked)} call{f.blocked === 1 ? " was" : "s were"} blocked by a safety rule before dialing (not in "Dialed").
        </Typography>
      )}
    </>
  );
};

/** Outcome counts as chips, biggest first. */
export const OutcomeChips = ({ outcomes }) => {
  const entries = Object.entries(outcomes || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <Typography variant="body2" color="text.secondary">No calls yet.</Typography>;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {entries.map(([key, n]) => {
        const o = OUTCOMES[key] || CALL_STATES[key] || { label: reasonText(key) || key, color: "default" };
        return <Chip key={key} size="small" color={o.color} variant="outlined" label={`${o.label}: ${formatNumber(n)}`} />;
      })}
    </Box>
  );
};

/* ------------------------------------------------------------------ by hour / day */

/** Results by hour or day of the answered call. kind: "hour" | "day" */
export const BreakdownTable = ({ rows, kind }) => {
  const list = (rows || []).slice().sort((a, b) =>
    kind === "day" ? DAY_NAMES.indexOf(a.label) - DAY_NAMES.indexOf(b.label) : String(a.label).localeCompare(String(b.label))
  );
  if (!list.length) return <Typography variant="body2" color="text.secondary">No answered calls yet.</Typography>;
  return (
    <TableContainer sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{kind === "day" ? "Day" : "Hour"}</TableCell>
            <TableCell align="right">Dialed</TableCell>
            <TableCell align="right">Answered</TableCell>
            <TableCell align="right">Paid</TableCell>
            <TableCell align="right">Paid rate</TableCell>
            <TableCell align="right">Revenue</TableCell>
            <TableCell align="right">Revenue per person</TableCell>
            <TableCell align="right">Cost</TableCell>
            <TableCell align="right">Profit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((r) => (
            <TableRow key={r.label}>
              <TableCell>{r.label}</TableCell>
              <TableCell align="right">{formatNumber(r.dialed)}</TableCell>
              <TableCell align="right">{formatNumber(r.people)}</TableCell>
              <TableCell align="right">{formatNumber(r.payers)}</TableCell>
              <TableCell align="right">{pct(r.payerRate)}</TableCell>
              <TableCell align="right">{formatBdt(r.revenue)}</TableCell>
              <TableCell align="right">{formatBdt(r.revenuePerPerson)}</TableCell>
              <TableCell align="right">{formatBdt(r.cost)}</TableCell>
              <TableCell align="right" sx={{ color: moneyColor(r.profit) }}>{formatBdt(r.profit)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const HourDayTables = ({ byHour, byDay }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} lg={6}>
      <Typography variant="subtitle2" gutterBottom>By hour (Bangladesh time)</Typography>
      <BreakdownTable rows={byHour} kind="hour" />
    </Grid>
    <Grid item xs={12} lg={6}>
      <Typography variant="subtitle2" gutterBottom>By day</Typography>
      <BreakdownTable rows={byDay} kind="day" />
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption" color="text.secondary">
        Grouped by when the member answered. "Answered" counts members; only hours and days with an answered call are shown.
      </Typography>
    </Grid>
  </Grid>
);

/* ------------------------------------------------------------------ campaign: holdout + pilot */

/** Everyone assigned to be called vs the holdout (or attributed when there is no holdout). */
export const ComparisonSection = ({ comparison }) => {
  if (!comparison) return null;
  const { method, sentGroup = {}, holdoutGroup = {}, extraRevenue, cost, profit } = comparison;
  const made = Number(profit) >= 0;
  const hasHoldout = method === "holdout" && (holdoutGroup.people || 0) > 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Compared with the holdout</Typography>
      {!hasHoldout && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No holdout group: this counts every payment by members who were picked to be called, including people who would
          have paid anyway, so the real profit is probably lower. Use a holdout on the next campaign to measure it properly.
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Metric label="Cost (calls + SMS)" value={formatBdt(cost)} />
        <Metric
          label="Revenue from the called group"
          value={formatBdt(sentGroup.revenue)}
          sub={`${formatNumber(sentGroup.payers)} payer${sentGroup.payers === 1 ? "" : "s"} of ${formatNumber(sentGroup.people)}`}
        />
        <Metric
          label={hasHoldout ? "Extra revenue from calling" : "Attributed revenue"}
          value={formatBdt(extraRevenue)}
          sub={hasHoldout ? "Compared with the holdout group" : "Not compared with a holdout"}
        />
        <Metric
          label={made ? "Profit" : "Loss"}
          value={formatBdt(Math.abs(Number(profit) || 0))}
          color={made ? "success.main" : "error.main"}
          sub={Number(cost) > 0 && Number(extraRevenue) > 0 ? `Revenue = ${(extraRevenue / cost).toFixed(1)}× the cost` : undefined}
        />
      </Grid>
      {hasHoldout && (
        <TableContainer sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Group</TableCell>
                <TableCell align="right">People</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Paid rate</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Revenue per person</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[["Picked to be called", sentGroup], ["Holdout (not called)", holdoutGroup]].map(([label, g]) => (
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">{formatNumber(g.people)}</TableCell>
                  <TableCell align="right">{formatNumber(g.payers)}</TableCell>
                  <TableCell align="right">{pct(g.payerRate)}</TableCell>
                  <TableCell align="right">{formatBdt(g.revenue)}</TableCell>
                  <TableCell align="right">{formatBdt(g.revenuePerUser)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" display="block">
        Everyone picked to be called counts, including members who didn't answer or were skipped by a safety rule.
      </Typography>
    </Box>
  );
};

const PILOT_LABELS = { holdout: "Holdout (nothing)", sms: "SMS only", sms_call: "SMS + AI call" };
const PILOT_ORDER = ["holdout", "sms", "sms_call"];

/** Pilot: holdout / SMS only / SMS + AI call. */
export const PilotTable = ({ pilot }) => {
  if (!Array.isArray(pilot) || !pilot.length) return null;
  const rows = pilot.slice().sort((a, b) => PILOT_ORDER.indexOf(a.label) - PILOT_ORDER.indexOf(b.label));
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Pilot: three groups</Typography>
      <TableContainer sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Group</TableCell>
              <TableCell align="right">People</TableCell>
              <TableCell align="right">Payers</TableCell>
              <TableCell align="right">Payer rate</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Revenue per person</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Extra revenue vs holdout</TableCell>
              <TableCell align="right">Profit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((g) => {
              const isHoldout = g.label === "holdout";
              return (
                <TableRow key={g.label}>
                  <TableCell>{PILOT_LABELS[g.label] || g.label}</TableCell>
                  <TableCell align="right">{formatNumber(g.people)}</TableCell>
                  <TableCell align="right">{formatNumber(g.payers)}</TableCell>
                  <TableCell align="right">{pct(g.payerRate)}</TableCell>
                  <TableCell align="right">{formatBdt(g.revenue)}</TableCell>
                  <TableCell align="right">{formatBdt(g.revenuePerPerson)}</TableCell>
                  <TableCell align="right">{formatBdt(g.cost)}</TableCell>
                  <TableCell align="right">{isHoldout ? "—" : formatBdt(g.extraRevenue)}</TableCell>
                  <TableCell align="right" sx={{ color: isHoldout ? undefined : moneyColor(g.profit) }}>
                    {isHoldout ? "—" : formatBdt(g.profit)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block">
        Extra revenue = (revenue per person − the holdout's revenue per person) × people. Profit = extra revenue − the group's cost.
      </Typography>
    </Box>
  );
};

/* ------------------------------------------------------------------ whole reports */

/** Report of one campaign (fetches GET /ai-calling/report?campaignId=…). */
export const CampaignReport = ({ campaignId, refreshMs = 0, title = "Results" }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (quiet = false) => {
      if (!campaignId) return;
      if (!quiet) setLoading(true);
      const res = await aiCallApi.report({ campaignId });
      if (res.ok) {
        setReport(res.data);
        setError("");
      } else if (!quiet) {
        setError(res.error);
      }
      setLoading(false);
    },
    [campaignId]
  );

  useEffect(() => {
    setReport(null);
    load();
  }, [load]);
  useInterval(() => load(true), refreshMs, !!refreshMs);

  const action = (
    <Button size="small" startIcon={<Refresh />} onClick={() => load()} disabled={loading}>Refresh</Button>
  );

  if (loading && !report) {
    return (
      <SectionCard title={title} action={action}>
        <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} /></Box>
      </SectionCard>
    );
  }
  if (error && !report) return <SectionCard title={title} action={action}><ErrorState error={error} onRetry={() => load()} /></SectionCard>;
  if (!report) return null;

  return (
    <SectionCard title={title} action={action}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Chip variant="outlined" label={`Payments within ${report.windowDays} day${report.windowDays === 1 ? "" : "s"}`} />
        {report.windowOpenUntil && <Chip color="info" variant="outlined" label={`Still counting until ${dhaka(report.windowOpenUntil)}`} />}
        {report.campaign?.pilot && <Chip color="secondary" variant="outlined" label="Pilot" />}
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Funnel</Typography>
      <FunnelRow funnel={report.funnel} />

      <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>Outcomes</Typography>
      <Box sx={{ mb: 3 }}><OutcomeChips outcomes={report.outcomes} /></Box>

      <ComparisonSection comparison={report.comparison} />
      <PilotTable pilot={report.pilot} />

      <HourDayTables byHour={report.byHour} byDay={report.byDay} />

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Counts completed bKash payments and wallet top-ups within {report.windowDays} day{report.windowDays === 1 ? "" : "s"}.
        The funnel counts payments after an answered call; the holdout comparison counts everyone from launch.
      </Typography>
    </SectionCard>
  );
};

/** All AI calls in a period (data from GET /ai-calling/report?days=…). */
const usd = (n) => `$${(Number(n) || 0).toFixed(4)}`;

/** The AI part of the cost (Soniox speech + Claude), from each call's saved usage. The phone line is separate. */
const AICostSection = ({ aiCost }) => {
  if (!aiCost) return null;
  const earlier = (aiCost.calls || 0) - (aiCost.withBreakdown || 0);
  return (
    <SectionCard title="AI cost" subtitle="What the AI providers charged for the calls: Soniox (hearing and speaking) and Claude (the replies). The phone line is separate.">
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Metric label="AI cost" value={usd(aiCost.usd)} sub={`${formatBdt(aiCost.bdt)} · ${formatNumber(aiCost.calls)} call${aiCost.calls === 1 ? "" : "s"}`} />
        <Metric label="Per call" value={aiCost.calls ? usd(aiCost.usd / aiCost.calls) : "—"} />
        <Metric label="Hearing + speaking (Soniox)" value={usd(aiCost.sttUsd + aiCost.ttsUsd)} sub={`${usd(aiCost.sttUsd)} + ${usd(aiCost.ttsUsd)}`} />
        <Metric
          label="Replies (Claude)"
          value={usd(aiCost.claudeUsd + (aiCost.summaryUsd || 0))}
          sub={aiCost.summaryUsd > 0 ? `${usd(aiCost.claudeUsd)} + ${usd(aiCost.summaryUsd)} older call summaries` : undefined}
        />
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block">
        From each call's own usage × the providers' prices (test calls not included here).
        {earlier > 0 && ` ${formatNumber(earlier)} call${earlier === 1 ? " is" : "s are"} from before 31 Oct 2026 and only ${earlier === 1 ? "has" : "have"} a total (${usd(aiCost.earlierUsd)}).`}
      </Typography>
    </SectionCard>
  );
};

export const OverallReport = ({ report, onReload }) => {
  const navigate = useNavigate();
  if (!report) return null;
  const campaigns = report.campaigns || [];
  return (
    <>
      <SectionCard title={`Last ${report.days} day${report.days === 1 ? "" : "s"}`} subtitle="All AI calls except test calls">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip variant="outlined" label={`Payments within ${report.windowDays} day${report.windowDays === 1 ? "" : "s"} after an answered call`} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Funnel</Typography>
        <FunnelRow funnel={report.funnel} />
        <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>Outcomes</Typography>
        <OutcomeChips outcomes={report.outcomes} />
      </SectionCard>

      <SectionCard title="By campaign">
        {campaigns.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No calls in this period.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell align="right">Calls</TableCell>
                  <TableCell align="right">Answered</TableCell>
                  <TableCell align="right">Payers</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.map((c) => {
                  const isCampaign = isObjectId(c.key);
                  const name = isCampaign ? (c.name && c.name !== c.key ? c.name : "Deleted campaign") : PURPOSES[c.key] || reasonText(c.key);
                  return (
                    <TableRow
                      key={c.key}
                      hover={isCampaign}
                      className={isCampaign ? "aic-row-link" : undefined}
                      onClick={isCampaign ? () => navigate(`/ai-calling/campaigns/${c.key}`) : undefined}
                    >
                      <TableCell>{name}</TableCell>
                      <TableCell align="right">{formatNumber(c.calls)}</TableCell>
                      <TableCell align="right">{formatNumber(c.answered)}</TableCell>
                      <TableCell align="right">{formatNumber(c.payers)}</TableCell>
                      <TableCell align="right">{formatBdt(c.revenue)}</TableCell>
                      <TableCell align="right">{formatBdt(c.cost)}</TableCell>
                      <TableCell align="right" sx={{ color: moneyColor(c.profit) }}>{formatBdt(c.profit)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Revenue here is every payment after an answered call (not compared with a holdout). Open a campaign for its holdout comparison.
        </Typography>
      </SectionCard>

      <AICostSection aiCost={report.aiCost} />

      <SectionCard title="When calls work best">
        <HourDayTables byHour={report.byHour} byDay={report.byDay} />
      </SectionCard>
    </>
  );
};
